import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useSystemSettings, useUpdateSystemSettings } from '@/hooks/useSettings';

const DEFAULT_TEMPLATE = 'Partial payment for {jobCode} — {jobDescription}';
const DEFAULT_OVERDUE_DAYS = 30;
// Matches the DB defaults in backend/src/db/schema.ts — only used as a
// fallback before the settings query resolves.
const DEFAULT_EMAIL_SUBJECT_TEMPLATE = 'Invoice {invoiceNumber} from {companyName}';
const DEFAULT_EMAIL_BODY_TEMPLATE =
  'Hi,\n\n' +
  'Please find attached invoice {invoiceNumber} for {jobCode}, in the amount of {amount}.\n\n' +
  'If you have any questions about this invoice, just reply to this email.\n\n' +
  'Thank you,\n{companyName}';

// FR-4.4/4.5. Deliberately no {dueDate} — there are no payment terms, and an
// unknown token renders literally in the client-facing email.
const EMAIL_TEMPLATE_TOKENS = '{invoiceNumber} {jobCode} {clientName} {amount} {companyName}';

export default function BillingSettings() {
  const { data: settings, isLoading } = useSystemSettings();
  const updateSettings = useUpdateSystemSettings();

  const [template, setTemplate] = useState('');
  const [overdueDays, setOverdueDays] = useState(String(DEFAULT_OVERDUE_DAYS));
  const [emailSubjectTemplate, setEmailSubjectTemplate] = useState('');
  const [emailBodyTemplate, setEmailBodyTemplate] = useState('');
  const [emailReplyTo, setEmailReplyTo] = useState('');

  useEffect(() => {
    if (settings) {
      setTemplate(settings.partialInvoiceDescriptionTemplate ?? DEFAULT_TEMPLATE);
      setOverdueDays(String(settings.overdueAfterDays ?? DEFAULT_OVERDUE_DAYS));
      setEmailSubjectTemplate(settings.invoiceEmailSubjectTemplate ?? DEFAULT_EMAIL_SUBJECT_TEMPLATE);
      setEmailBodyTemplate(settings.invoiceEmailBodyTemplate ?? DEFAULT_EMAIL_BODY_TEMPLATE);
      setEmailReplyTo(settings.invoiceEmailReplyTo ?? '');
    }
  }, [settings]);

  const preview = template
    .replace('{jobCode}', 'NC-26-001')
    .replace('{jobDescription}', 'Kitchen remodel');

  const parsedOverdueDays = Number(overdueDays);
  const overdueDaysValid =
    Number.isInteger(parsedOverdueDays) && parsedOverdueDays >= 1 && parsedOverdueDays <= 365;
  const emailSubjectValid = emailSubjectTemplate.trim().length > 0;
  const emailBodyValid = emailBodyTemplate.trim().length > 0;

  const canSave = overdueDaysValid && emailSubjectValid && emailBodyValid;

  const handleSave = () => {
    if (!template.trim() || !canSave) return;
    updateSettings.mutate({
      partialInvoiceDescriptionTemplate: template,
      overdueAfterDays: parsedOverdueDays,
      invoiceEmailSubjectTemplate: emailSubjectTemplate,
      invoiceEmailBodyTemplate: emailBodyTemplate,
      invoiceEmailReplyTo: emailReplyTo.trim() ? emailReplyTo.trim() : null,
    });
  };

  const hasChanges = settings
    ? template !== (settings.partialInvoiceDescriptionTemplate ?? DEFAULT_TEMPLATE)
      || parsedOverdueDays !== (settings.overdueAfterDays ?? DEFAULT_OVERDUE_DAYS)
      || emailSubjectTemplate !== (settings.invoiceEmailSubjectTemplate ?? DEFAULT_EMAIL_SUBJECT_TEMPLATE)
      || emailBodyTemplate !== (settings.invoiceEmailBodyTemplate ?? DEFAULT_EMAIL_BODY_TEMPLATE)
      || emailReplyTo !== (settings.invoiceEmailReplyTo ?? '')
    : false;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Billing</h2>
        <p className="text-sm text-gray-500 mt-1">
          Configure defaults for invoices and billing.
        </p>
      </div>

      <div className="max-w-lg space-y-4">
        <div>
          <label htmlFor="descriptionTemplate" className="block text-sm font-medium text-gray-700 mb-1">
            Partial Invoice Description Template
          </label>
          <p className="text-xs text-gray-500 mb-1">
            Available placeholders: {'{jobCode}'}, {'{jobDescription}'}
          </p>
          <input
            id="descriptionTemplate"
            type="text"
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {template && (
            <p className="mt-2 text-xs text-gray-400">
              Preview: <span className="text-gray-600">{preview}</span>
            </p>
          )}
        </div>

        <div>
          <label htmlFor="overdueAfterDays" className="block text-sm font-medium text-gray-700 mb-1">
            Flag invoices overdue after
          </label>
          <p className="text-xs text-gray-500 mb-1">
            Counted from the date an invoice is sent. Individual clients can override this.
          </p>
          <div className="flex items-center gap-2">
            <input
              id="overdueAfterDays"
              type="number"
              min={1}
              max={365}
              value={overdueDays}
              onChange={(e) => setOverdueDays(e.target.value)}
              className="w-24 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <span className="text-sm text-gray-500">days</span>
          </div>
          {!overdueDaysValid && (
            <p className="mt-1 text-xs text-red-600">Enter a whole number of days between 1 and 365.</p>
          )}
        </div>

        <div className="pt-2 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900 mb-1">Invoice Email</h3>
          <p className="text-xs text-gray-500 mb-3">
            Sent to the client when an invoice is emailed out.
          </p>
        </div>

        <div>
          <label htmlFor="emailSubjectTemplate" className="block text-sm font-medium text-gray-700 mb-1">
            Email Subject
          </label>
          <textarea
            id="emailSubjectTemplate"
            rows={2}
            value={emailSubjectTemplate}
            onChange={(e) => setEmailSubjectTemplate(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="mt-1 text-xs text-gray-500">Available tokens: {EMAIL_TEMPLATE_TOKENS}</p>
          {!emailSubjectValid && (
            <p className="mt-1 text-xs text-red-600">Email subject cannot be empty.</p>
          )}
        </div>

        <div>
          <label htmlFor="emailBodyTemplate" className="block text-sm font-medium text-gray-700 mb-1">
            Email Body
          </label>
          <textarea
            id="emailBodyTemplate"
            rows={6}
            value={emailBodyTemplate}
            onChange={(e) => setEmailBodyTemplate(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="mt-1 text-xs text-gray-500">Available tokens: {EMAIL_TEMPLATE_TOKENS}</p>
          {!emailBodyValid && (
            <p className="mt-1 text-xs text-red-600">Email body cannot be empty.</p>
          )}
        </div>

        <div>
          <label htmlFor="emailReplyTo" className="block text-sm font-medium text-gray-700 mb-1">
            Reply-To Address
          </label>
          <p className="text-xs text-gray-500 mb-1">
            Where client replies land. Leave blank to use the sending address.
          </p>
          <input
            id="emailReplyTo"
            type="email"
            value={emailReplyTo}
            onChange={(e) => setEmailReplyTo(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <button
          onClick={handleSave}
          disabled={!hasChanges || !canSave || updateSettings.isPending}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {updateSettings.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Save
        </button>
      </div>
    </div>
  );
}
